import {type ButtonHTMLAttributes, type ReactNode, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {AlertCircle, CheckCircle, Loader2} from 'lucide-react';
import {Button} from '../button';
import {cn} from '../lib/cn';

export type ButtonStatuses =
  | 'pending'
  | 'error'
  | 'success'
  | 'idle'
  | 'validating'
  | 'loading';

export interface StatusButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  hidden?: boolean;
  status: ButtonStatuses;
  floating?: boolean;
  idleText?: string;
  loadingText?: string;
  floatingToHeader?: boolean;
  successText?: string;
  icon?: ReactNode;
  variant?: 'default' | 'destructive' | 'secondary' | 'ghost' | 'link';
}

export function StatusButton({
  hidden,
  status,
  className,
  floating,
  idleText,
  loadingText,
  floatingToHeader,
  successText,
  icon,
  variant = 'default',
  ...props
}: StatusButtonProps) {
  const {t} = useTranslation();
  const configMap = useMemo(() => {
    const defaultLoadingConfig = {
      text: loadingText ?? `${t('saving')}...`,
      icon: <Loader2 className="h-4 w-4 animate-spin" />,
      variant,
      isLoading: true,
    };

    return {
      pending: {...defaultLoadingConfig},
      loading: {...defaultLoadingConfig},
      validating: {...defaultLoadingConfig, text: t('validating')},
      error: {
        text: t('error'),
        icon: <AlertCircle className="h-4 w-4" />,
        variant: 'destructive',
        isLoading: false,
      },
      success: {
        text: successText ?? t('saved_exclamation'),
        icon: <CheckCircle className="h-4 w-4" />,
        variant,
        isLoading: false,
      },
      idle: {
        text: idleText ?? t('save_changes'),
        icon,
        variant,
        isLoading: false,
      },
    };
  }, [t, idleText, loadingText, successText, icon, variant]);

  const config = configMap[status] || configMap.idle;

  if (hidden) {
    return null;
  }

  return (
    <Button
      className={cn(
        'min-w-[140px]',
        {
          'fixed bottom-10 left-1/2 z-[5] -translate-x-1/2 transition-all duration-100 ease-out':
            floating,
          'absolute left-auto right-[32px] top-[22px] transform-none max-md:hidden md:flex':
            floatingToHeader,
        },
        className,
      )}
      variant={config.variant as 'default' | 'destructive'}
      readOnly={config.isLoading || status === 'success' || status === 'error'}
      {...props}
    >
      {config.icon}
      <span>{config.text}</span>
    </Button>
  );
}
